import { NO, UIColor, UIRoute, UITextView, UIView, UIViewBroadcastEvent, UIViewController, YES } from "uicore-ts"
import { RHProcurementPartOffer } from "../../../models/prodModels"
import { RHCore } from "../Core/RHCore"
import { RHIncomingOffer } from "../Core/RHOfferStore"
import { RHProcurementStore } from "../Core/RHProcurementStore"
import { CBDataViewCellDescriptorWithObject } from "../Custom components/CB/CBDataView"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHDataView } from "../Custom components/RH/RHDataView"
import { RHNotificationBadge } from "../Custom components/RH/RHNotificationBadge"
import { RHTableActionButtonView } from "../Custom components/RH/RHTableActionButtonView"
import { formatRHDateTimeWithLineBreak } from "../Custom components/RH/Utils_slop/RHDateFormatting"
import { RHBadges, RHColors, RHTables } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHEuroFormatter } from "../Custom components/RH/Utils_slop/RHEuroFormatter"
import { RHProcurementPartDisplay } from "../Custom components/RH/Utils_slop/RHProcurementPartDisplay"
import { RHTextSanitizer } from "../Custom components/RH/Utils_slop/RHTextSanitizer"
import { RHTimestampSorting } from "../Custom components/RH/Utils_slop/RHTimestampSorting"
import { RHIncomingOffersViewTokens, RHResponsiveTextTokens } from "../Custom components/RH/Utils_slop/RHViewTokens"
import { ProcurementDetailsViewController } from "../ProcurementDetails/ProcurementDetailsViewController"
import { populatedValueOfReference } from "../Core/RHReference"


export class IncomingOffersViewController extends UIViewController {
    
    
    readonly titleLabel = new UITextView(
        this.view.elementID + "TitleLabel",
        // UITextView.type.header2
        UITextView.type.header4
    ).configuredWithObject({
        text: "Pakkumused",
        textColor: RHColors.textPrimary,
        backgroundColor: UIColor.transparentColor
    }).addedAsSubviewToView(this.view)
    
    readonly unseenBadgeLabel = new UITextView(
        this.view.elementID + "UnseenBadge"
    ).configuredWithObject({
        backgroundColor: RHColor.primary,
        textColor: RHColor.textWhite,
        isSingleLine: true,
        fontSize: 12
    }).addedAsSubviewToView(this.view)
    
    readonly offersDataView = new RHDataView<RHIncomingOffer>().configuredWithObject({
        noDataMessage: "Sissetulevaid pakkumusi ei ole",
        descriptors: [
            CBDataViewCellDescriptorWithObject({
                title: "Viitenumber",
                keyPath: "procurement",
                allowSorting: NO,
                titleLabelAlignment: UITextView.textAlignment.left,
                cellLabelAlignment: UITextView.textAlignment.left,
                updateCell: (cellView, dataItem) => {
                    const procurement = this._procurementFor(dataItem)
                    cellView.titleLabel!.text = procurement?.referenceNumber?.toString() || "-"
                    // Unseen offer information is presented on the action title.
                    // No per-cell row tint: semi-transparent cell backgrounds left faint
                    // vertical seams at the weight-based column edges.
                    cellView.backgroundColor = UIColor.transparentColor
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Nimetus",
                keyPath: "procurement",
                allowSorting: NO,
                titleLabelAlignment: UITextView.textAlignment.left,
                cellLabelAlignment: UITextView.textAlignment.left,
                updateCell: (cellView, dataItem) => {
                    const procurement = this._procurementFor(dataItem)
                    cellView.titleLabel!.text = RHTextSanitizer.escapedHTML(
                        procurement?.title || "Nimetus puudub"
                    )
                    // Long titles wrap to (and clamp at) two lines; full title in the tooltip.
                    cellView.configureAsClampedMultilineText()
                    // Unseen offer information is presented on the action title.
                    // No per-cell row tint: semi-transparent cell backgrounds left faint
                    // vertical seams at the weight-based column edges.
                    cellView.backgroundColor = UIColor.transparentColor
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Pakkuja",
                keyPath: "offererCompany",
                // Not sortable: keyPath is the populated company OBJECT, so the sorter
                // would order rows by the object's JSON (ObjectId first), not the
                // displayed name — the caret would produce a misleading order.
                allowSorting: NO,
                titleLabelAlignment: UITextView.textAlignment.left,
                cellLabelAlignment: UITextView.textAlignment.left,
                updateCell: (cellView, dataItem) => {
                    cellView.titleLabel!.text = populatedValueOfReference(dataItem.offererCompany)?.name || "-"
                    // Unseen offer information is presented on the action title.
                    // No per-cell row tint: semi-transparent cell backgrounds left faint
                    // vertical seams at the weight-based column edges.
                    cellView.backgroundColor = UIColor.transparentColor
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Osasid",
                keyPath: "partOffers",
                allowSorting: NO,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem) => {
                    const procurement = this._procurementFor(dataItem)
                    const procurementPartCount = procurement?.parts?.length
                    if (!RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(procurementPartCount)) {
                        cellView.titleLabel!.text = ""
                        cellView.backgroundColor = UIColor.transparentColor
                        return
                    }
                    
                    const activeParts = (dataItem.partOffers as RHProcurementPartOffer[]) ?? []
                    const submittedParts: number = new Set(
                        activeParts
                            .filter(p => !p.isCancelled)
                            .map(p => p.partId?.toString())
                            .filter(Boolean)
                    ).size
                    
                    cellView.titleLabel!.text = submittedParts > 0 ? `${submittedParts}` : "-"
                    // Unseen offer information is presented on the action title.
                    // No per-cell row tint: semi-transparent cell backgrounds left faint
                    // vertical seams at the weight-based column edges.
                    cellView.backgroundColor = UIColor.transparentColor
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Esitamise kuupäev",
                keyPath: "submittedAt",
                allowSorting: YES,
                // Task 3 — default to most-recently-submitted offer first. Production
                // renders one row per offer (not per procurement like the mock-up);
                // sorting by the offer's own submit time desc is the production-native
                // expression of "newest offer activity at the top".
                initialOrderingState: "descending",
                dataType: "string",
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem) => {
                    if (dataItem.submittedAt) {
                        const date = new Date(dataItem.submittedAt)
                        cellView.titleLabel!.text =
                            formatRHDateTimeWithLineBreak(date)
                        cellView.titleLabel!.isSingleLine = NO
                    }
                    else {
                        cellView.titleLabel!.text = "-"
                    }
                    // Unseen offer information is presented on the action title.
                    // No per-cell row tint: semi-transparent cell backgrounds left faint
                    // vertical seams at the weight-based column edges.
                    cellView.backgroundColor = UIColor.transparentColor
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Tähtaeg",
                keyPath: "procurement",
                // Not sortable: the store strips the populated procurement off the row
                // (cell renders from the procurement cache), so the sorter has no value
                // here and the caret would silently not order by deadline.
                allowSorting: NO,
                dataType: "string",
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem) => {
                    const procurement = this._procurementFor(dataItem)
                    const deadlineDate = procurement?.deadlineDate
                    if (deadlineDate) {
                        const date = new Date(deadlineDate)
                        const isExpired = date < new Date()
                        cellView.titleLabel!.text =
                            formatRHDateTimeWithLineBreak(date)
                        cellView.titleLabel!.isSingleLine = NO
                        cellView.titleLabel!.textColor = isExpired ? RHColor.textMuted : RHColor.textBlack
                    }
                    else {
                        cellView.titleLabel!.text = "-"
                        cellView.titleLabel!.textColor = RHColor.textBlack
                    }
                    // Unseen offer information is presented on the action title.
                    // No per-cell row tint: semi-transparent cell backgrounds left faint
                    // vertical seams at the weight-based column edges.
                    cellView.backgroundColor = UIColor.transparentColor
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: RHResponsiveTextTokens.tableHeaderLabels.estimatedCost.regular,
                keyPath: "procurement",
                // Not sortable: the store strips the populated procurement off the row
                // (cell computes the cost from the procurement cache), so the sorter
                // has no numeric value here and the caret would be a no-op.
                allowSorting: NO,
                dataType: "number",
                // Monetary amounts read best right-aligned (consistent across all tables).
                titleLabelAlignment: UITextView.textAlignment.right,
                cellLabelAlignment: UITextView.textAlignment.right,
                updateCell: (cellView, dataItem) => {
                    const procurement = this._procurementFor(dataItem)
                    const estimatedCost = RHProcurementStore.totalEstimatedCost(procurement)
                    cellView.titleLabel!.text = RHEuroFormatter.formattedAmountOrDash(estimatedCost)
                    // Unseen offer information is presented on the action title.
                    // No per-cell row tint: semi-transparent cell backgrounds left faint
                    // vertical seams at the weight-based column edges.
                    cellView.backgroundColor = UIColor.transparentColor
                }
            }),
            CBDataViewCellDescriptorWithObject<RHIncomingOffer, RHTableActionButtonView>({
                title: "Toimingud",
                keyPath: "_id",
                allowSorting: NO,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                initCustomView: () => new RHTableActionButtonView(),
                updateCell: (cellView, dataItem) => {
                    // New offer content and messages are unread information.
                    const hasUnreadOfferUpdate = this._hasUnreadOfferUpdate(dataItem)
                    const hasUnreadMessage = RHCore.sharedInstance.notificationStore.offerMessageCount(
                        dataItem.procurementId
                    ) > 0
                    let tooltip = "Lugemata sõnum."
                    if (hasUnreadOfferUpdate) {
                        tooltip = "Lugemata pakkumusega seotud teavitus."
                    }
                    cellView.customView.text = "Halda"
                    RHNotificationBadge.applyUnreadEmphasis(
                        cellView.customView.button.titleLabel,
                        hasUnreadOfferUpdate || hasUnreadMessage,
                        tooltip
                    )
                    cellView.backgroundColor = UIColor.transparentColor
                    cellView.customView.buttonWasPressed = () => {
                        const procurementId = dataItem.procurementId
                        if (procurementId) {
                            UIRoute.currentRoute
                                .routeWithViewControllerComponent(
                                    ProcurementDetailsViewController,
                                    { id: procurementId, tab: "offers", backTo: IncomingOffersViewController.routeComponentName }
                                )
                                .routeByRemovingComponentNamed(
                                    IncomingOffersViewController.routeComponentName
                                )
                                .apply()
                        }
                    }
                }
            })
        ]
    }).addedAsSubviewToView(this.view)
    
    readonly bottomSpacerView = new UIView().addedAsSubviewToView(this.view)
    
    /**
     * Unread incoming-offer presentation is deadline-gated so confidential
     * offer activity is not exposed through a contextual list before the
     * procurer is allowed to open the offers. It covers both initial submission
     * and a later modification of the submitted offer.
     */
    _hasUnreadOfferUpdate(dataItem: RHIncomingOffer): boolean {
        if ((dataItem.unseenOfferNotificationCount ?? 0) === 0) {
            return NO
        }
        const procurement = this._procurementFor(dataItem)
        const deadlineDate = procurement?.deadlineDate
        if (!deadlineDate) {
            return NO
        }
        return new Date(deadlineDate) < new Date()
    }
    
    /**
     * Synchronous procurement lookup from the store's in-memory cache.
     * Always returns a valid result after _reloadData() has pre-warmed
     * the cache via Promise.all(getProcurement) for all offer IDs.
     */
    private _procurementFor(dataItem: RHIncomingOffer) {
        return RHCore.sharedInstance.procurementStore.cachedProcurement(dataItem.procurementId)
    }
    
    private _unseenCountDidChangeHandler = (_event: UIViewBroadcastEvent) => {
        this._updateUnseenBadge()
    }
    
    constructor(view: UIView) {
        super(view)
        this.view.configureWithObject({
            backgroundColor: RHColor.background
        })
        RHNotificationBadge.configurePageLabel(this.unseenBadgeLabel)
        this.unseenBadgeLabel.hidden = true
        this._applyTableColumnLayout()
    }
    
    
    private _applyTableColumnLayout() {
        this.offersDataView.tableHeaderView.configureWithObject({
            cellWeights: RHIncomingOffersViewTokens.columnWeights,
            intrinsicContentHeight: () => 64
        })
        this.offersDataView.tableView.configureWithObject({
            cellWeights: RHIncomingOffersViewTokens.columnWeights
        })
        this._configureTableHeaderCells()
        this.offersDataView.tableHeaderView.setNeedsLayout()
        this.offersDataView.tableView.setNeedsLayout()
    }
    
    private _configureTableHeaderCells() {
        for (const headerCell of this.offersDataView.tableHeaderView.cells) {
            const buttonView = headerCell.buttonView
            if (buttonView) {
                buttonView.contentPadding = 2
            }
            const titleLabel = buttonView?.titleLabel ?? headerCell.titleLabel
            if (!titleLabel) {
                continue
            }
            titleLabel.isSingleLine = NO
            titleLabel.style.whiteSpace = "normal"
            titleLabel.style.lineHeight = RHIncomingOffersViewTokens.multilineHeaderLineHeight
            titleLabel.textElementView.style.whiteSpace = "normal"
            titleLabel.textElementView.style.lineHeight = RHIncomingOffersViewTokens.multilineHeaderLineHeight
        }
    }
    
    
    static override readonly routeComponentName = "incoming_offers"
    static override readonly ParameterIdentifierName = {}
    static readonly requiredApplicationModes: ("procurer" | "offerer")[] = ["procurer"]
    
    override async viewWillAppear() {
        await super.viewWillAppear()
        RHCore.sharedInstance.offerStore.subscribe(this, () => { this._reloadData() })
        RHCore.sharedInstance.procurementStore.subscribe(this, () => { this._reloadData() })
        RHCore.sharedInstance.notificationStore.subscribe(this, (affectedIds) => {
            if (affectedIds.length > 0) {
                // Acknowledgement state patches RHOfferStore's cached row
                // metadata before this callback. Rebuild the data-view copies
                // from that cache so unseenOfferNotificationCount is not stale.
                void this._reloadData()
                return
            }
            this.offersDataView.tableView.reloadData()
        })
        RHCore.sharedInstance.offerStore.invalidateIncomingOffers()
        this.view.performFunctionAfterBroadcastEvent(
            RHCore.broadcastEventName.unseenEventCountDidChange,
            this._unseenCountDidChangeHandler
        )
    }
    
    override async viewWillDisappear() {
        await super.viewWillDisappear()
        RHCore.sharedInstance.offerStore.unsubscribe(this)
        RHCore.sharedInstance.procurementStore.unsubscribe(this)
        RHCore.sharedInstance.notificationStore.unsubscribe(this)
        this.view.removePerformFunctionAfterBroadcastEvent(
            RHCore.broadcastEventName.unseenEventCountDidChange,
            this._unseenCountDidChangeHandler
        )
    }
    
    override async viewDidAppear() {
        await super.viewDidAppear()
        // Read-only nav badge refresh — opening this list does NOT mark offers
        // as seen; per-procurement unread emphasis clears when the procurer opens the
        // procurement's offers tab.
        RHCore.sharedInstance.notificationStore.refreshGlobalCounts()
        RHCore.sharedInstance.notificationStore.reloadOfferMessageCounts()
        this.view.setNeedsLayout()
    }

    _updateUnseenBadge() {
        // Page pill remains hidden because the shared navigation title carries the signal.
        this.unseenBadgeLabel.hidden = true
        this.view.setNeedsLayout()
    }
    
    async _reloadData() {
        try {
            const offers = await RHCore.sharedInstance.offerStore.getIncomingOffers()
            
            // Pre-warm the procurement cache for all unique IDs before
            // rendering so that _procurement(for:) returns synchronously
            // from cache in every cell descriptor.
            const uniqueIds = [...new Set(offers.map(offer => offer.procurementId).filter(Boolean))]
            await Promise.all(
                uniqueIds.map(id => RHCore.sharedInstance.procurementStore.getProcurement(id))
            )

            // Normalise submittedAt to a canonical ISO string so the data view's
            // string-based descending sort reliably means "newest submitted first".
            this.offersDataView.data = (offers ?? []).map(offer => ({
                ...offer,
                submittedAt: RHTimestampSorting.sortableISOString(offer.submittedAt)
            }))
        }
        catch (error) {
            console.error("IncomingOffersViewController: error loading offers:", error)
            this.offersDataView.data = []
        }
        this._updateUnseenBadge()
    }
    
    override async handleRoute(route: UIRoute) {
        await super.handleRoute(route)
    }
    
    
    override layoutViewSubviews() {
        super.layoutViewSubviews()
        
        const padding = this.core.paddingLength
        const labelHeight = padding * 2
        const badgeHeight = RHBadges.notificationPageHeight
        const badgePaddingH = RHBadges.notificationPagePaddingX
        const bounds = this.view.contentBoundsWithInset(padding)
        
        const titleFrame = bounds.rectangleWithHeight(labelHeight)
        this.titleLabel.frame = titleFrame
        
        if (!this.unseenBadgeLabel.hidden) {
            const badgeWidth = this.unseenBadgeLabel.intrinsicContentWidth(badgeHeight) + badgePaddingH * 2
            this.unseenBadgeLabel.frame = titleFrame
                .rectangleWithWidth(badgeWidth, 1)
                .rectangleWithHeight(badgeHeight, 0.5)
            this.unseenBadgeLabel.setBorder(badgeHeight / 2, 0)
        }
        
        titleFrame
            .rectangleForNextRow(padding, RHTables.dataViewPageHeight)
            .assignedAsFrameOfView(this.offersDataView)
            .rectangleForNextRow(padding, this.bottomSpacerView)
            .assignedAsFrameOfView(this.bottomSpacerView)
    }
    
}
